import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  LayoutDashboardIcon, 
  PackageIcon, 
  ShoppingCartIcon, 
  UsersIcon, 
  BarChartIcon, 
  SettingsIcon,
  LogOutIcon,
  UserIcon,
  CoinsIcon,
  FileTextIcon
} from 'lucide-react';
import { 
  Sidebar, 
  SidebarHeader, 
  SidebarContent, 
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton
} from '../ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import CloudflareImage from '../ui/cloudflare-image';
// import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { logoutAdmin, adminApi } from '../../services/adminApi';

const navItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboardIcon,
    id: 'dashboard',
    path: '/root-admin-dashboard'
  },
  { title: 'Analytics', icon: BarChartIcon, id: 'analytics', path: '/admin/analytics' },
  {
    title: 'Products',
    icon: PackageIcon,
    id: 'products',
    path: '/admin/products',
    subItems: [
      { title: 'All Products', path: '/admin/products' },
      { title: 'Add Product', path: '/admin/products/add' },
      { title: 'Categories', path: '/admin/categories' },
      { title: 'Tags', path: '/admin/tags' },
      { title: 'Size Charts', path: '/admin/size-charts' },
      { title: 'Coupons', path: '/admin/coupons' },
      { title: 'Gift Cards', path: '/admin/gift-cards' }
    ]
  },
  { title: 'Orders', icon: ShoppingCartIcon, id: 'orders', path: '/admin/orders' },
  { title: 'Customers', icon: UsersIcon, id: 'customers', path: '/admin/customers' },
  { title: 'Referrals', icon: UsersIcon, id: 'referrals', path: '/admin/referrals' },
  { title: 'MAD Coins', icon: CoinsIcon, id: 'mad-coins', path: '/admin/mad-coins' },
  { title: 'Settings', icon: SettingsIcon, id: 'settings', path: '/admin/settings' },
  { title: 'Banner Management', icon: SettingsIcon, id: 'banner', path: '/admin/banner' },
  {
    title: 'Logs',
    icon: FileTextIcon,
    id: 'logs',
    path: '/admin/logs',
    subItems: [
      { title: 'All Logs', path: '/admin/logs' },
      { title: 'Deleted Logs', path: '/admin/deleted-logs' },
    ]
  },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const [adminUser, setAdminUser] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [expandedMenus, setExpandedMenus] = useState({
    products: true,
    logs: true
  });

  useEffect(() => {
    // Fetch admin profile from API since we don't store in localStorage
    const fetchAdminProfile = async () => {
      try {
        const response = await adminApi.get('/api/admin/profile');
        if (response.data.success) {
          setAdminUser(response.data.admin);
        }
      } catch (error) {
        console.error('Failed to fetch admin profile:', error);
      }
    };

    fetchAdminProfile();
  }, []);
  
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/admin/products') || path.includes('/admin/categories') || path.includes('/admin/tags') || path.includes('/admin/size-charts')) {
      setActiveSection('products');
    } else if (path.includes('/admin/customers')) {
      setActiveSection('customers');
    } else if (path.includes('/root-admin-dashboard')) {
      setActiveSection('dashboard');
    }
  }, [navigate]);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin-login');
  };

  const handleNavClick = (sectionId, path) => {
    setActiveSection(sectionId);
    if (path && path !== '#') {
      navigate(path);
    }

    if (navItems.find(item => item.id === sectionId)?.subItems) {
      setExpandedMenus(prev => ({
        ...prev,
        [sectionId]: !prev[sectionId]
      }));
    }
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar className="hidden w-64 md:block">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton>
                <CloudflareImage src="/images/TMZ L.png" alt="TheMadZone Logo" className="h-8" />
                <span className="text-base font-semibold">TheMadZone</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenu>
            {navItems.map((item) => (
              <React.Fragment key={item.title}>
                <SidebarMenuItem>
                  <Button
                    variant={activeSection === item.id ? 'secondary' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => handleNavClick(item.id, item.path)}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.title}
                    {item.subItems && (
                      <span className="ml-auto">
                        {expandedMenus[item.id] ? (
                          <ArrowUpIcon className="h-4 w-4" />
                        ) : (
                          <ArrowDownIcon className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </Button>
                </SidebarMenuItem>

                {item.subItems && expandedMenus[item.id] && (
                  <div className="ml-7 pl-2 border-l border-gray-200">
                    {item.subItems.map((subItem) => (
                      <SidebarMenuItem key={subItem.title}>
                         <Button
                            asChild
                            variant={window.location.pathname === subItem.path ? 'secondary' : 'ghost'}
                            className="w-full justify-start"
                          >
                            <Link to={subItem.path}>{subItem.title}</Link>
                          </Button>
                      </SidebarMenuItem>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter>
          <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
            <LogOutIcon className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </SidebarFooter>
      </Sidebar>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center border-b bg-background px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <LayoutDashboardIcon className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </div>
            <h1 className="text-lg font-semibold md:text-xl">
              {activeSection === 'dashboard' && 'Admin Dashboard'}
              {activeSection === 'customers' && 'Customer Management'}
              {activeSection === 'products' && 'Product Management'}
              {activeSection === 'orders' && 'Order Management'}
              {activeSection === 'analytics' && 'Analytics'}
              {activeSection === 'referrals' && 'Referral Management'}
              {activeSection === 'mad-coins' && 'MAD Coins Management'}
              {activeSection === 'settings' && 'Settings'}
              {activeSection === 'logs' && 'Log Management'}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <UserIcon className="h-5 w-5" />
                  <span className="sr-only">Toggle user menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="font-medium">{adminUser?.name}</div>
                  <div className="text-xs text-muted-foreground">{adminUser?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
               
                <DropdownMenuItem>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {typeof children === 'function' ? children({ activeSection }) : children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
